import { Flex, Text, useDisclosure } from '@chakra-ui/react';
import { FC, useEffect, useRef, useState } from 'react';
import {
  DEFAULT_BLUR_STYLES_LEFT,
  DEFAULT_BLUR_STYLES_RIGHT,
  ScrollOrientation,
  useShowBlurOnScrollable,
} from '~/apps/consumer/hooks/useShowBlurOnScrollable';

interface Section {
  id: string;
  label: string;
}

interface ScrollTabsProps {
  sections: Section[];
  rootMargin?: string;
  threshold?: number[] | number;
}

export const ScrollTabs: FC<ScrollTabsProps> = ({
  sections,
  rootMargin = '-50% 0px',
  threshold = 0,
}) => {
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const {
    // isOpen: isScrolling,
    onOpen: onScrollStart,
    onClose: onScrollEnd,
  } = useDisclosure();

  const { showRightBlur, showLeftBlur, setInitialBlurRef, handleBlurOnScroll } =
    useShowBlurOnScrollable(ScrollOrientation.HORIZONTAL);

  useEffect(() => {
    // Create intersection observer to detect which section is in view
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const isSection = sections.some(
              (section) => section.id === entry.target.id
            );
            if (isSection) {
              setActiveSectionId(entry.target.id);
              const tabId = `${entry.target.id}-scroll-tab`;
              const tab = document.getElementById(tabId);
              tab?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
              });
            }
          }
        });
      },
      {
        threshold,
        rootMargin,
      }
    );

    // Observe all sections
    sections.forEach((section) => {
      const element = document.getElementById(section.id);
      if (element) {
        observerRef.current?.observe(element);
      }
    });

    return () => {
      observerRef.current?.disconnect();
    };
  }, [sections, rootMargin, threshold]);

  useEffect(() => {
    // Set initial active section if none is set
    if (!activeSectionId && sections.length > 0) {
      const firstSection = sections[0];
      setActiveSectionId(firstSection.id);
    }
  }, [activeSectionId, sections]);

  const handleTabClick = (sectionId: string) => {
    const element = document.getElementById(sectionId);

    if (element) {
      onScrollStart();
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });

      // Reset scrolling state after animation
      setTimeout(() => {
        onScrollEnd();
      }, 1000);
    }
  };

  return (
    <Flex minWidth="0" position="relative" width={{ base: '100%', md: 'auto' }}>
      <Flex
        ref={setInitialBlurRef}
        _after={{
          display: showRightBlur ? 'block' : 'none',
          ...DEFAULT_BLUR_STYLES_RIGHT,
        }}
        _before={{
          display: showLeftBlur ? 'block' : 'none',
          ...DEFAULT_BLUR_STYLES_LEFT,
        }}
        alignItems="center"
        gap={2}
        overflowX="auto"
        px={4}
        whiteSpace="nowrap"
        width={{ base: '100%', md: 'auto' }}
        onScroll={handleBlurOnScroll}
      >
        {sections.map((section) => (
          <ScrollTab
            key={section.id}
            id={section.id}
            isActive={activeSectionId === section.id}
            label={section.label}
            onClick={() => handleTabClick(section.id)}
          />
        ))}
      </Flex>
    </Flex>
  );
};

const ScrollTab = ({
  id,
  label,
  onClick,
  isActive,
}: {
  id: string;
  label: string;
  onClick: () => void;
  isActive: boolean;
}) => {
  return (
    <Flex
      alignItems="center"
      bg={isActive ? 'whiteAlpha.200' : 'transparent'}
      borderRadius="full"
      cursor="pointer"
      id={`${id}-scroll-tab`}
      justifyContent="center"
      px={3}
      py={1.5}
      onClick={onClick}
    >
      <Text fontWeight="medium">{label}</Text>
    </Flex>
  );
};
